import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { listComments, type CommentDTO } from '../api/comments'

type ListingRef = { id: string | number; title?: string; image?: string | null }

export function useListingsComments(listings: ListingRef[], opts?: { perListing?: number; batch?: number }) {
  const perListing = opts?.perListing ?? 2
  const batch = opts?.batch ?? 4

  const [limit, setLimit] = useState<number>(batch) // number of listings to fetch comments for
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [byListing, setByListing] = useState<Map<string | number, CommentDTO[]>>(new Map())

  const slice = useMemo(() => listings.slice(0, limit), [listings, limit])
  const fetchedIdsRef = useRef<Set<string | number>>(new Set())

  const load = useCallback(async () => {
    if (!slice.length) return
    setLoading(true)
    setError(null)
    try {
      // Fetch comments for listings not yet fetched
      const toGet = slice.filter((l) => !fetchedIdsRef.current.has(l.id))
      if (toGet.length === 0) return
      const results = await Promise.all(
        toGet.map(async (l) => {
          try {
            const res = await listComments({ type: 'listing', id: l.id, sort: 'top' })
            const parents = res.items.filter((c) => !c.parent_id)
            return { id: l.id, comments: parents.slice(0, perListing) }
          } catch (e) {
            return { id: l.id, comments: [] as CommentDTO[] }
          }
        })
      )
      setByListing((prev) => {
        const next = new Map(prev)
        for (const item of results) {
          fetchedIdsRef.current.add(item.id)
          next.set(item.id, item.comments)
        }
        return next
      })
    } catch (e: any) {
      setError(e?.message || 'Failed to load comments')
    } finally {
      setLoading(false)
    }
  }, [slice, perListing])

  useEffect(() => {
    // Reset when listings change entirely (new person)
    setByListing(new Map())
    fetchedIdsRef.current = new Set()
    setLimit(batch)
  }, [listings, batch])

  useEffect(() => {
    void load()
  }, [load])

  const canLoadMore = useMemo(() => limit < listings.length, [limit, listings.length])
  const loadMore = useCallback(() => setLimit((n) => Math.min(listings.length, n + batch)), [listings.length, batch])

  // Flatten with listing context
  const items = useMemo(() => {
    const flat: Array<{ listingId: string | number; comment: CommentDTO }> = []
    for (const [id, comments] of byListing.entries()) {
      for (const c of comments) flat.push({ listingId: id, comment: c })
    }
    // Sort newest first if timestamps available
    flat.sort((a, b) => {
      const at = a.comment.created_at ? new Date(a.comment.created_at).getTime() : 0
      const bt = b.comment.created_at ? new Date(b.comment.created_at).getTime() : 0
      return bt - at
    })
    return flat
  }, [byListing])

  return { items, loading, error, canLoadMore, loadMore }
}

